import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import CakeIcon from '@mui/icons-material/Cake';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import SaveIcon from '@mui/icons-material/Save';
import SectionCard from './SectionCard';
import { useUserContext } from '../../hooks/useUserContext';
import userService from '../../services/userService';
import tenantService from '../../services/tenantService';
import { config } from '../../config';
import { getApiErrorInfo } from '../../services/api';
import { formatUtcDate } from '../../utils/dateTime';

/** Backend rejects birth dates outside this range (see UserService). */
const MIN_BIRTH_DATE = '1900-01-01';
const todayDateInput = () => new Date().toISOString().substring(0, 10);

interface ProfileInfoCardProps {
  notify: (message: string) => void;
}

/** Name, email, firm, and birth date display plus the edit dialog. */
export default function ProfileInfoCard({ notify }: ProfileInfoCardProps) {
  const { clerkUser: user, backendProfile, updateClerkUserName, refreshUser } = useUserContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tenantName, setTenantName] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', birthDate: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const savedBirthDate = backendProfile?.birthDate?.substring(0, 10) ?? '';

  const isOnDefaultTenant = !backendProfile?.tenantId || backendProfile.tenantId === config.defaultTenantId;

  useEffect(() => {
    if (!backendProfile?.tenantId || isOnDefaultTenant) {
      setTenantName(null);
      return;
    }
    tenantService.getTenantById(backendProfile.tenantId).then(tenant => {
      if (tenant?.name) setTenantName(tenant.name);
    }).catch(() => {
      // Firm name is decorative; omit it if the lookup fails.
    });
  }, [backendProfile?.tenantId, isOnDefaultTenant]);

  const handleOpen = () => {
    setForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      birthDate: savedBirthDate,
    });
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.firstName.trim()) {
      setError('First name is required');
      return;
    }
    if (!backendProfile?.id) return;
    setSaving(true);
    setError('');
    try {
      await userService.updateMyName(form.firstName.trim(), form.lastName.trim());
      updateClerkUserName(form.firstName.trim(), form.lastName.trim());
      if (form.birthDate !== savedBirthDate) {
        // Anchors birthdate-based CPE reporting periods; the backend re-prefills
        // any unconfirmed license windows when this changes.
        await userService.updateMyProfileDetails(
          form.birthDate ? { birthDate: form.birthDate } : { clearBirthDate: true }
        );
        await refreshUser();
      }
      setDialogOpen(false);
      notify('Profile updated');
    } catch (err) {
      const apiError = getApiErrorInfo(err);
      setError(apiError.status === 400 ? apiError.message : 'Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      icon={<BadgeIcon />}
      title="Profile Information"
      action={
        <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={handleOpen}>
          Edit
        </Button>
      }
    >
      <Typography variant="h5" sx={{ mb: 1 }}>
        {user?.firstName || ''} {user?.lastName || ''}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EmailIcon color="action" fontSize="small" />
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {user?.primaryEmailAddress?.emailAddress || ''}
        </Typography>
      </Box>
      {tenantName && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
          <BusinessIcon color="action" fontSize="small" />
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {tenantName}
          </Typography>
        </Box>
      )}
      {savedBirthDate && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
          <CakeIcon color="action" fontSize="small" />
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Born {formatUtcDate(savedBirthDate)}
          </Typography>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="First Name *"
              value={form.firstName}
              onChange={e => setForm(prev => ({ ...prev, firstName: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={form.lastName}
              onChange={e => setForm(prev => ({ ...prev, lastName: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Birth Date"
              type="date"
              value={form.birthDate}
              onChange={e => setForm(prev => ({ ...prev, birthDate: e.target.value }))}
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { min: MIN_BIRTH_DATE, max: todayDateInput() },
              }}
              helperText="Optional. Some states calculate your CPE reporting period from your birth date."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !form.firstName.trim()}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
}
